import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useDebounce } from "@uidotdev/usehooks";
import { useMemo, useState } from "react";

import { createHead } from "@/components/createHead";
import { LoadMoreSpinner } from "@/components/LoadMoreSpinner";
import {
  MarketplaceBody,
  MarketplaceTitle,
} from "@/components/MarketplacePage";
import { AdsResult } from "@/components/PublicSearchResultGrid";
import { SearchInput } from "@/components/ui/search-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { getMonetizedFeedsQueryOptions, GetPublicFeedOptions } from "@/lib/api";

export const Route = createFileRoute("/_marketplace-layout/ads/")({
  component: RouteComponent,
  loader: async ({ context: { queryClient } }) => {
    await queryClient.prefetchInfiniteQuery(
      getMonetizedFeedsQueryOptions({
        page: 1,
        page_size: 12,
        sort_by: "popularity",
        search: "",
      }),
    );
  },
  head: () => {
    return createHead({
      title: "Advertise on Bluesky Feeds with Graze",
      ogTitle: "Advertise on Bluesky Feeds with Graze",
      description:
        "Graze is the first and only way to run sponsored content on Bluesky - see our partnered custom feeds here!",
      pathname: "/ads",
      ogImage: "/ads/og.png",
    });
  },
});

function AdsResultGrid({
  search,
  sort,
  showAdsBio = false,
}: {
  search?: string;
  sort: GetPublicFeedOptions["sort_by"];
  showAdsBio?: boolean;
}) {
  const { data, fetchNextPage, isLoading } = useInfiniteQuery(
    getMonetizedFeedsQueryOptions({
      page: 1,
      page_size: 12,
      sort_by: sort,
      search,
    }),
  );

  const allResults = useMemo(() => {
    return data?.pages.flatMap((d) => d.results) || [];
  }, [data]);

  const total = data?.pages[0]?.total_items || 0;

  let items = null;

  if (allResults.length > 0) {
    items = allResults.map((item) => {
      return (
        <AdsResult
          key={item.algorithm.id}
          item={item}
          showAdsBio={showAdsBio}
        />
      );
    });
  } else {
    items = Array.from({ length: 12 }).map((_, index) => {
      return (
        <Skeleton
          // eslint-disable-next-line @eslint-react/no-array-index-key
          key={"skeleton-" + index}
          className="group p-4 border border-slate-6 dark:border-slatedark-6 rounded-lg overflow-hidden"
          style={{ height: 180 }}
        />
      );
    });
  }

  return (
    <div className="grid">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items}
      </div>
      {allResults.length < total && allResults.length > 0 && (
        <LoadMoreSpinner fetchNextPage={fetchNextPage} isLoading={isLoading} />
      )}
    </div>
  );
}

function RouteComponent() {
  const [search, setSearch] = useState("");
  const [sort, setSort] =
    useState<GetPublicFeedOptions["sort_by"]>("popularity");
  const activeSearch = useDebounce(search, 500);

  return (
    <>
      <MarketplaceTitle>Ads</MarketplaceTitle>
      <MarketplaceBody>
        <div className="flex gap-2 max-w-[600px] w-full mx-auto mb-10">
          <SearchInput
            value={search}
            onChange={setSearch}
            aria-label="Search feeds with advertising enabled"
            className="border-2 border-teal-7 dark:border-tealdark-7 bg-teal-6 dark:bg-tealdark-6 text-teal-normal text-lg! h-auto"
          />
          <Select
            value={sort}
            onValueChange={(v) => setSort(v as GetPublicFeedOptions["sort_by"])}
          >
            <SelectTrigger
              aria-label="Sort by"
              variant="action"
              className="w-[140px] shrink-0 border-2 border-teal-7 dark:border-tealdark-7 bg-teal-6 dark:bg-tealdark-6 text-teal-normal text-lg h-auto"
            >
              <SelectValue placeholder="Select one of your feeds" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_at">Created</SelectItem>
              <SelectItem value="updated_at">Updated</SelectItem>
              <SelectItem value="name">Name</SelectItem>
              <SelectItem value="complexity">Complexity</SelectItem>
              <SelectItem value="popularity">Popularity</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <AdsResultGrid search={activeSearch} sort={sort} />
      </MarketplaceBody>
    </>
  );
}
